import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import AdminPortalLayout from './AdminPortalLayout';
import {
  AdminMe, PlanRow, fetchPlans, updatePlan,
} from '../lib/admin-portal-api';
import { FaCheckCircle, FaExclamationTriangle, FaDollarSign } from '../lib/icons';

interface Props { me: AdminMe }

const DURATIONS: PlanRow['duration'][] = ['monthly', 'quarterly', 'half-yearly', 'yearly'];

export default function AdminPortalPlans({ me }: Props) {
  const [rows, setRows] = useState<PlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [drafts, setDrafts] = useState<Record<number, Partial<PlanRow>>>({});

  useEffect(() => {
    fetchPlans()
      .then((res) => setRows(res.data || []))
      .catch(() => setMsg({ type: 'error', text: 'Failed to load plans' }))
      .finally(() => setLoading(false));
  }, []);

  if (!me.isSuperAdmin) return <Navigate to="/admin-portal" replace />;

  const onPatch = (id: number, k: keyof PlanRow, v: any) => {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [k]: v } }));
  };

  const onSave = async (p: PlanRow) => {
    const draft = drafts[p.id];
    if (!draft || Object.keys(draft).length === 0) return;
    setSavingId(p.id); setMsg(null);
    try {
      await updatePlan(p.id, draft);
      setRows((list) => list.map((x) => x.id === p.id ? { ...x, ...draft } as PlanRow : x));
      setDrafts((d) => { const c = { ...d }; delete c[p.id]; return c; });
      setMsg({ type: 'success', text: `Plan "${p.name || p.id}" updated.` });
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.error || 'Save failed' });
    } finally { setSavingId(null); }
  };

  const onCancel = (id: number) => setDrafts((d) => { const c = { ...d }; delete c[id]; return c; });

  return (
    <AdminPortalLayout me={me} active="plans">
      <Helmet><title>Plans — EatMeat Admin</title></Helmet>

      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Subscription Plans</h1>
        <p className="text-sm text-gray-500 mt-1">Edit prices, currency, duration, and platform product IDs. Amounts are in cents.</p>
      </div>

      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm flex items-start gap-2 mb-4 ${
          msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800'
                                 : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {msg.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
          {msg.text}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 bg-white rounded-2xl animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
          <FaDollarSign className="text-4xl mx-auto mb-3 text-gray-200" />
          <p className="text-sm text-gray-500">No plans defined.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((p) => {
            const draft = drafts[p.id] || {};
            const merged = { ...p, ...draft };
            const dirty = Object.keys(draft).length > 0;
            return (
              <div key={p.id} className={`bg-white rounded-2xl border ${dirty ? 'border-primary/30 ring-2 ring-primary/10' : 'border-gray-100'} shadow-sm p-5`}>
                <div className="flex items-center justify-between mb-3">
                  <input
                    value={merged.name || ''}
                    onChange={(e) => onPatch(p.id, 'name', e.target.value)}
                    className="font-bold text-gray-800 text-lg bg-transparent border-b border-dashed border-gray-200 focus:border-primary outline-none w-full"
                    placeholder="Plan name"
                  />
                  <button
                    onClick={() => onPatch(p.id, 'is_active', !merged.is_active)}
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${merged.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {merged.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Amount (cents)">
                    <input
                      type="number"
                      value={merged.amount ?? ''}
                      onChange={(e) => onPatch(p.id, 'amount', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none"
                    />
                    {typeof merged.amount === 'number' && (
                      <div className="text-[10px] text-gray-400 mt-0.5">≈ {merged.currency || ''} {(merged.amount / 100).toFixed(2)}</div>
                    )}
                  </Field>
                  <Field label="Currency">
                    <input
                      value={merged.currency || ''}
                      onChange={(e) => onPatch(p.id, 'currency', e.target.value.toUpperCase())}
                      placeholder="USD"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none"
                    />
                  </Field>
                  <Field label="Duration">
                    <select
                      value={merged.duration || ''}
                      onChange={(e) => onPatch(p.id, 'duration', (e.target.value || null) as PlanRow['duration'])}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none"
                    >
                      <option value="">—</option>
                      {DURATIONS.map((d) => <option key={d || 'na'} value={d || ''}>{d}</option>)}
                    </select>
                  </Field>
                  <Field label="Apple product id">
                    <input
                      value={merged.apple_product_id || ''}
                      onChange={(e) => onPatch(p.id, 'apple_product_id', e.target.value)}
                      placeholder="com.eatmeat.monthly"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none font-mono"
                    />
                  </Field>
                  <Field label="Stripe price id">
                    <input
                      value={merged.stripe_price_id || ''}
                      onChange={(e) => onPatch(p.id, 'stripe_price_id', e.target.value)}
                      placeholder="price_..."
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none font-mono"
                    />
                  </Field>
                  <Field label="Stripe product id">
                    <input
                      value={merged.stripe_product_id || ''}
                      onChange={(e) => onPatch(p.id, 'stripe_product_id', e.target.value)}
                      placeholder="prod_..."
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none font-mono"
                    />
                  </Field>
                </div>

                {dirty && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2 justify-end">
                    <button onClick={() => onCancel(p.id)} disabled={savingId === p.id}
                      className="text-xs px-3 py-1.5 rounded-md bg-gray-50 hover:bg-gray-100 font-semibold disabled:opacity-50">Cancel</button>
                    <button onClick={() => onSave(p)} disabled={savingId === p.id}
                      className="text-xs px-3 py-1.5 rounded-md bg-primary text-white hover:opacity-90 font-semibold disabled:opacity-50">
                      {savingId === p.id ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminPortalLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}
